// Adapted from beautifului.dev (MIT, © 2026 Shane Levine).
import { useEffect, useId, useMemo, useState } from 'react';
import {
  CaretDown,
  Check,
  CheckCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { ChatToolActivity, ChatTurn } from '@/slices/chat/chatSlice';
import ChatThinkingIndicator, {
  formatElapsed,
  useElapsedMs,
} from './ChatThinkingIndicator';

/** How long the finished trail stays open before it folds itself away. */
const AUTO_COLLAPSE_MS = 300;

/** `search_content` -> `Search content`, so an unmapped tool still reads. */
const humanizeToolName = (name: string): string => {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
};

/** Display aliases let saved activity retain its meaning after copy updates. */
const activityLabel = (label: string): string =>
  label === 'Checking how Ora works' ? 'Checking documentation' : label;

const ToolActivityLine = ({ call }: { call: ChatToolActivity }) => (
  <li className="flex items-start gap-2 text-xs text-fg-secondary">
    <span className="mt-0.5 shrink-0" aria-hidden="true">
      {call.status === 'running' ? (
        <Spinner size={12} thickness={3} />
      ) : call.status === 'ok' ? (
        <CheckCircle size={12} weight="fill" />
      ) : (
        <WarningCircle size={12} weight="fill" className="text-status-danger" />
      )}
    </span>
    <span className="min-w-0">
      <span className="text-fg-primary">{humanizeToolName(call.name)}</span>
      {call.summary ? <span> — {call.summary}</span> : null}
    </span>
  </li>
);

/**
 * One step of the turn. `running` is the step happening right now, which gets
 * a quiet dot rather than a check it has not earned yet.
 */
const StepLine = ({
  label,
  running,
  delayMs,
}: {
  label: string;
  running: boolean;
  delayMs: number;
}) => (
  <p
    className="animate-chat-fade-up m-0! flex items-start gap-2 text-xs leading-4! text-fg-secondary"
    style={{ animationDelay: `${delayMs}ms` }}
  >
    <span
      className="mt-0.5 flex size-3 shrink-0 items-center justify-center"
      aria-hidden="true"
    >
      {running ? (
        <span className="size-1.5 rounded-full bg-fg-secondary opacity-60" />
      ) : (
        <Check size={12} weight="bold" />
      )}
    </span>
    <span className="min-w-0">{label}</span>
  </p>
);

interface ChatTurnActivityProps {
  turn: ChatTurn;
  /**
   * Draw one line per `tool.call`/`tool.result` pair, and keep them after the
   * turn ends. For the dev harness (`/dev/chat`) only: a student never sees
   * a tool's name, arguments, or result summary (ORA-514).
   */
  showToolActivity?: boolean;
}

/**
 * What the runtime did for one turn: the loader and the live trail while it
 * runs, then a one-line "Thought for 4s · 3 steps" summary the student can
 * open again. A turn can take several tool rounds (ORA-586), so the trail is
 * what tells the student the turn is moving rather than stuck (ORA-587), and
 * keeping it after the answer lands is what lets them check the companion's
 * work instead of taking it on trust (ORA-597). It still leaves with the turn
 * — and a turn the server persisted a trail for comes back with it on reload
 * (ORA-604), rebuilt into a finished turn by the slice.
 */
const ChatTurnActivity = ({
  turn,
  showToolActivity = false,
}: ChatTurnActivityProps) => {
  const isWorking = turn.status === 'requested' || turn.status === 'streaming';
  const isDone = turn.status === 'done';
  const steps = turn.statusTrail;
  const elapsedMs = useElapsedMs(turn.startedAt, isWorking);

  const calls = useMemo(
    () =>
      showToolActivity
        ? turn.toolCallIds.flatMap((callId) =>
            turn.toolCalls[callId] ? [turn.toolCalls[callId]] : []
          )
        : [],
    [showToolActivity, turn.toolCallIds, turn.toolCalls]
  );

  /**
   * Null until the student takes over the disclosure; after that their choice
   * wins over the automatic one for the rest of the turn's life on screen.
   */
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  /**
   * Held open briefly after the turn ends so the last check registers — but a
   * turn restored from history (ORA-604) never ran on this screen, so it
   * arrives already settled and draws its summary line folded rather than
   * flashing the whole trail open on every reload.
   */
  const [settled, setSettled] = useState(turn.startedAt == null);
  const rowsId = useId();

  useEffect(() => {
    if (!isDone) {
      setSettled(false);
      return;
    }

    const timeout = setTimeout(() => setSettled(true), AUTO_COLLAPSE_MS);
    return () => clearTimeout(timeout);
  }, [isDone]);

  if (!isWorking && !isDone) return null;
  if (steps.length === 0 && calls.length === 0) return null;

  const expanded = isWorking ? true : (manualExpanded ?? !settled);
  // The runtime's own duration if it sent one, otherwise what we measured.
  const summaryMs = turn.ms ?? (turn.startedAt == null ? null : elapsedMs);

  const summary =
    [
      summaryMs == null ? null : `Thought for ${formatElapsed(summaryMs)}`,
      steps.length === 0
        ? null
        : `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`,
    ]
      .filter(Boolean)
      .join(' · ') || 'What Ora did';

  return (
    <div
      // The same surface as a follow-up chip, so the two read as one family
      // wherever the thread is drawn (the light-mode desktop paints it on
      // the water).
      className="chat-motion mb-2 flex flex-col rounded-xl border border-border-subtle bg-surface-canvas px-3 py-2 dark:bg-surface-raised"
      data-testid="chat-turn-activity"
    >
      {isWorking ? (
        <ChatThinkingIndicator
          label={turn.statusLabel ? activityLabel(turn.statusLabel) : undefined}
          startedAt={turn.startedAt}
        />
      ) : (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={rowsId}
          onClick={() => setManualExpanded((current) => !(current ?? expanded))}
          className="animate-chat-fade-in -mx-1.5 flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-lg px-1.5 text-left text-sm text-fg-secondary outline-none hover:bg-surface-highlight focus-visible:ring-[3px] focus-visible:ring-ring/50 mobile:min-h-8"
        >
          <span>{summary}</span>
          <CaretDown
            size={14}
            weight="bold"
            aria-hidden="true"
            className={cn(
              'shrink-0 transition-transform duration-300 ease-chat-out',
              expanded && 'rotate-180'
            )}
          />
        </button>
      )}

      <div
        id={rowsId}
        className="grid transition-[grid-template-rows,opacity] duration-[400ms] ease-chat-out"
        style={{
          gridTemplateRows: expanded ? '1fr' : '0fr',
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="overflow-hidden">
          <div
            className="mt-1 ml-[5px] flex flex-col gap-1 border-l border-border-subtle py-0.5 pl-3"
            data-testid="chat-turn-steps"
          >
            {steps.map((label, index) => (
              <StepLine
                key={`${index}-${label}`}
                label={activityLabel(label)}
                running={isWorking && index === steps.length - 1}
                // While the turn runs the steps arrive one at a time and are
                // staggered by the stream itself; the cascade is only for the
                // finished trail, which appears all at once.
                delayMs={isWorking ? 0 : Math.min(index, 5) * 120}
              />
            ))}
            {calls.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {calls.map((call) => (
                  <ToolActivityLine key={call.callId} call={call} />
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatTurnActivity;
